// [NEW FILE] PM-13: the rating scale is limited to two choices, 0 to 5 or 1 to 10.
//
// The backend keeps every score on 0–100 (score cap 100) and has no "scale" field, so (without a backend change)
// the chosen scale is stored in the rating-scheme code: STANDARD_S05 = 0 to 5, STANDARD_S10 = 1 to 10.
// Band thresholds are typed on the chosen scale and converted to 0–100 when saved.
// Scores are shown on the chosen scale wherever the scheme is known (users who can read the scoring configuration).
import { formatScore } from "./format";

export const SCALES = {
  FIVE: { key: "FIVE", suffix: "_S05", label: "0 to 5", min: 0, max: 5, step: 0.1 },
  TEN: { key: "TEN", suffix: "_S10", label: "1 to 10", min: 1, max: 10, step: 1 },
};

// PM-9 uses the same code for the automatic PIP rule: STANDARD_S05_PIP2 = a PIP draft starts automatically when the
// final rating is in the lowest 2 bands (PIP0 or no tag = off).
const CODE = /(?:_S(05|10))?(?:_PIP(\d))?$/;

export const scaleFromCode = (code = "") => {
  const m = String(code || "").toUpperCase().match(CODE);
  if (!m || !m[1]) return null;
  return m[1] === "05" ? SCALES.FIVE : SCALES.TEN;
};
export const pipBandsFromCode = (code = "") => Number(String(code || "").toUpperCase().match(CODE)?.[2] || 0);

// [NEW] Backend migration 016: rating_scheme.scale ("0_5" | "1_10") and rating_scheme.pip_rule
// ({ enabled, lowest_band_count }). Cycles and records also return rating_scale, so every user knows the scale.
// Schemes saved by this frontend carry the same values in their code (STANDARD_S05_PIP1), so the code is read first;
// schemes without that code use the backend fields. Works with the old and the new backend.
export const SCALE_FIELD = { FIVE: "0_5", TEN: "1_10" };
export const scaleFromField = (value) => {
  const v = String(value || "").toUpperCase();
  if (v === "0_5") return SCALES.FIVE;
  if (v === "1_10") return SCALES.TEN;
  return null;
};
const parseRule = (rule) => {
  if (!rule) return {};
  if (typeof rule === "string") { try { return JSON.parse(rule) || {}; } catch { return {}; } }
  return rule;
};
export const scaleOfScheme = (scheme) =>
  scaleFromCode(scheme?.scheme_code) || scaleFromField(scheme?.scale ?? scheme?.rating_scale);
// [UPDATED] Requirement 6: the rule is read from the scheme code first (STANDARD_PIP1). The frontend keeps the rule
// there and saves pip_rule as switched off, so the backend never STARTS a plan by itself; the frontend creates a
// DRAFT instead (services/autoPipApi.js). Older schemes without a coded rule still use pip_rule.
export const pipBandsOfScheme = (scheme) => {
  const fromCode = pipBandsFromCode(scheme?.scheme_code);
  if (fromCode) return fromCode;
  const rule = parseRule(scheme?.pip_rule);
  if (typeof rule.enabled === "boolean") return rule.enabled ? Number(rule.lowest_band_count) || 1 : 0;
  return 0;
};
// [UPDATED] Scoring & ratings is back to the original page (no scale choice): scores show on 0-100 again.
export const SCALE_CHOICE_ENABLED = false;
// The pip_rule body the backend expects for "lowest N bands" (0 = off).
export const pipRuleFor = (n) => (Number(n) > 0
  ? { enabled: true, lowest_band_count: Number(n), reviewer: "MANAGER" }
  : { enabled: false });

export const baseCode = (code = "STANDARD") => String(code || "STANDARD").toUpperCase().replace(CODE, "") || "STANDARD";
export const codeFor = (base, scale, pipBands = 0) => `${baseCode(base)}${scale.suffix}${pipBands ? `_PIP${pipBands}` : ""}`;

const round = (n, d = 2) => Math.round(Number(n) * 10 ** d) / 10 ** d;
// 0–100 score → value on the scale, and back.
export const toScale = (score, scale) => round(scale.min + (Number(score) * (scale.max - scale.min)) / 100);
export const fromScale = (value, scale) => round(((Number(value) - scale.min) * 100) / (scale.max - scale.min));

// The scale used to show scores, set when the rating scheme is loaded (services/core.js).
let displayScale = null;
export const setDisplayScale = (scale) => { displayScale = scale || null; };
export const getDisplayScale = () => displayScale;

// PM-9: the automatic PIP rule of the scheme that was loaded last (number of lowest bands, 0 = off).
let pipBands = 0;
export const setPipBands = (n) => { pipBands = Number(n) || 0; };
export const getPipBands = () => pipBands;

// A 0–100 score as text on the current scale ("3.6 / 5"), or on 0–100 when no scale is known (older schemes).
export function formatRatingScore(score, digits = 1) {
  if (score === null || score === undefined || Number.isNaN(Number(score))) return "—";
  if (!displayScale) return formatScore(score, digits);
  return `${formatScore(toScale(score, displayScale), 1)} / ${displayScale.max}`;
}
